import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { cn } from "@/lib/utils";
import * as Icons from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";

const ADMIN_NAV = [
  { to: "/", label: "Dashboard", icon: "LayoutDashboard" },
  { to: "/drivers", label: "Drivers", icon: "Users" },
  { to: "/vans", label: "Vans", icon: "Truck" },
  { to: "/stops", label: "Daily Stops", icon: "MapPin" },
  { to: "/dpd-import", label: "DPD Import", icon: "Download" },
  { to: "/earnings", label: "Earnings", icon: "PoundSterling" },
  { to: "/ticket-finder", label: "Ticket Finder", icon: "Search" },
  { to: "/tickets", label: "Tickets", icon: "Receipt" },
  { to: "/mileage", label: "Mileage", icon: "Gauge" },
  { to: "/fuel", label: "Fuel Control", icon: "Fuel" },
  { to: "/fuel-budget", label: "Fuel Budget", icon: "Wallet" },
  { to: "/expenses", label: "Vehicle Costs", icon: "Wrench" },
  { to: "/profit", label: "Profit", icon: "TrendingUp" },
  { to: "/reports", label: "Reports", icon: "FileText" },
  { to: "/audit", label: "Audit Log", icon: "ScrollText" },
  { to: "/settings", label: "Settings", icon: "Settings" },
];

const MANAGER_NAV = ADMIN_NAV.filter((n) => !["/settings"].includes(n.to));

const DRIVER_NAV = [
  { to: "/", label: "My Dashboard", icon: "LayoutDashboard" },
  { to: "/my-stops", label: "My Stops", icon: "MapPin" },
  { to: "/my-fuel", label: "Fuel Log", icon: "Fuel" },
  { to: "/my-tickets", label: "My Tickets", icon: "Receipt" },
];

function navFor(role) {
  if (role === "admin") return ADMIN_NAV;
  if (role === "manager") return MANAGER_NAV;
  return DRIVER_NAV;
}

const NavItems = ({ items, onNavigate }) => (
  <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-2">
    {items.map((item) => {
      const Icon = Icons[item.icon] || Icons.Circle;
      return (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.to === "/"}
          onClick={onNavigate}
          data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
          className={({ isActive }) =>
            cn(
              "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
              isActive
                ? "bg-primary/15 text-primary"
                : "text-muted-foreground hover:bg-accent hover:text-foreground"
            )
          }
        >
          <Icon className="h-4 w-4 shrink-0" />
          <span className="truncate">{item.label}</span>
        </NavLink>
      );
    })}
  </nav>
);

const Brand = () => (
  <div className="flex items-center gap-2.5 px-5 py-5 border-b border-border">
    <div className="h-9 w-9 rounded-xl bg-primary grid place-items-center">
      <Icons.Truck className="h-5 w-5 text-primary-foreground" />
    </div>
    <div>
      <p className="font-display font-extrabold text-foreground leading-none">Near Way</p>
      <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">UK Fleet Manager</p>
    </div>
  </div>
);

const UserFooter = () => {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  return (
    <div className="border-t border-border p-3 space-y-2">
      <div className="flex items-center gap-2 rounded-lg px-2 py-2">
        <div className="h-8 w-8 rounded-full bg-primary/20 text-primary grid place-items-center text-xs font-bold uppercase">
          {(user?.name || "?").slice(0, 2)}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium truncate text-foreground">{user?.name}</p>
          <p className="text-[11px] uppercase tracking-wide text-primary font-semibold">{user?.role}</p>
        </div>
      </div>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" className="flex-1" onClick={toggle} data-testid="theme-toggle">
          {theme === "dark" ? <Icons.Sun className="h-4 w-4" /> : <Icons.Moon className="h-4 w-4" />}
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="flex-1"
          onClick={async () => {
            await logout();
            navigate("/login");
          }}
          data-testid="logout-button"
        >
          <Icons.LogOut className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default function Sidebar() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const items = navFor(user?.role);

  return (
    <>
      {/* Desktop */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-[260px] flex-col bg-card border-r border-border z-30">
        <Brand />
        <NavItems items={items} />
        <UserFooter />
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-40 flex items-center justify-between bg-card/90 backdrop-blur border-b border-border px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-primary grid place-items-center">
            <Icons.Truck className="h-4 w-4 text-primary-foreground" />
          </div>
          <span className="font-display font-extrabold">Near Way</span>
        </div>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" size="icon" data-testid="mobile-menu-button">
              <Icons.Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-[260px] flex flex-col">
            <SheetTitle className="sr-only">Navigation menu</SheetTitle>
            <Brand />
            <NavItems items={items} onNavigate={() => setOpen(false)} />
            <UserFooter />
          </SheetContent>
        </Sheet>
      </div>
    </>
  );
}
